export default {
  toasts: {
    load_error: 'Failed to load payment setup',
    rails_saved: 'Payment rails saved',
    gateway_registered: 'Gateway registered',
    redact_failed: 'Failed to redact the gateway',
    gateway_redacted: 'Gateway redacted',
    sca_provisioned: '3DS2 registration created',
    company_assigned: 'Restaurant assigned',
    default_set: 'Default gateway set — new restaurants will be assigned to it automatically',
    default_failed: 'Failed to set the default gateway',
    primary_switched: 'Primary gateway switched to {{gateway}}',
    primary_switch_failed: 'Failed to switch the primary gateway',
  },
  custody: {
    title: 'Who processes payments',
    description:
      'Decided once, on the first rail save, and locked afterwards: with its own gateway the partner holds the funds; on the FoodAmigos Adyen account FoodAmigos holds them and pays the partner’s share out monthly.',
    options: {
      provider: 'Partner’s own gateway',
      platform: 'FoodAmigos processes payments',
    },
    locked_notice: 'Locked to “{{custodian}}” by the payment configuration — it cannot be changed.',
    pick_first: 'Choose who processes payments first.',
    lock_confirm: {
      title: 'Lock payments to “{{custodian}}”?',
      description:
        'Saving now decides who holds this partner’s funds, and that cannot be undone — not here, and not by support. The payment rail can still be changed later; who processes the payments cannot.',
      confirm: 'Lock and save',
    },
  },
  rails: {
    title: 'Payment rail',
    description:
      'Which processor this partner runs on — one at a time. Restaurants can only be switched to the rail selected here, and a rail cannot be replaced while restaurants still charge on it.',
    save: 'Save rail',
    multiple_warning:
      'This partner still runs on more than one rail ({{rails}}). Saving keeps only the one selected above; the rest are removed, and the change is refused outright if any restaurant still charges on one of them.',
    options: {
      stripe: 'Stripe',
      adyen: 'Adyen',
      spreedly: 'Local gateway via Spreedly',
    },
    platform_stripe_unavailable: 'Stripe on the FoodAmigos accounts is not available yet.',
    platform_custody_notice: 'Not available when FoodAmigos processes the payments.',
    provider_custody_adyen_notice:
      'Not available when the partner brings its own gateway — Adyen is the FoodAmigos processing rail.',
  },
  spreedly_section_title: 'Spreedly setup',
  gateways: {
    title: 'Gateways',
    description: 'The partner’s own PSP accounts. The whole charge settles to the selected gateway’s acquirer.',
    register: 'Register gateway',
    empty: 'No gateways registered yet',
    columns: {
      gateway: 'Gateway',
      status: 'Status',
      sca: '3DS2',
      companies: 'Restaurants',
    },
    statuses: {
      active: 'Active',
      redacted: 'Redacted',
    },
    sca: {
      sandbox: '3DS2 sandbox',
      live: '3DS2 live',
      none: 'No 3DS2',
    },
    default_tag: 'Default',
    actions: {
      make_default: 'Make default',
      check: 'Run check…',
      sca_provision: 'Set up 3DS2…',
      sca_manage: '3DS2 registration…',
      redact: 'Redact gateway',
    },
  },
  redact_confirm: {
    title: 'Redact {{gateway}}?',
    description:
      'The credentials are deleted at Spreedly and cannot be restored. {{count}} assigned restaurant(s) still charge through this gateway — while any are assigned, the redaction is refused and you are asked to confirm again.',
    redact: 'Redact',
    force_title: 'Redact {{gateway}} anyway?',
    force_description:
      '{{count}} restaurant(s) are still assigned to this gateway. Redacting it now will refuse their checkout until each is assigned another gateway. This cannot be undone.',
    force_redact: 'Redact anyway',
  },
  companies: {
    title: 'Restaurants',
    description: 'Which processor each restaurant charges through, and its Spreedly gateway assignment.',
    empty: 'No restaurants',
    no_gateway: 'None',
    unassigned: 'Not assigned',
    // The default gateway is assigned at creation and never fails the creation —
    // so a restaurant can end up unassigned with the reason only in the log.
    assignment_error_label: 'Automatic assignment failed',
    assign: 'Assign',
    reassign: 'Reassign',
    columns: {
      restaurant: 'Restaurant',
      primary_gateway: 'Primary gateway',
      spreedly: 'Spreedly gateway',
    },
    switch_primary_to: 'Switch to {{gateway}}',
    assign_modal: {
      title: 'Assign {{company}} to a gateway',
      assign: 'Assign',
      gateway: 'Gateway',
      make_primary: 'Also make Spreedly the primary gateway',
      make_primary_hint: 'Charges only route through Spreedly while it is the primary gateway.',
    },
  },
  register_modal: {
    title: 'Register a gateway',
    register: 'Register',
    gateway_type: 'Gateway type',
    gateway_type_placeholder: 'Search Spreedly’s catalog…',
    auth_mode: 'Authentication mode',
    no_credentials: 'This gateway type needs no credentials (Spreedly sandbox).',
    description: 'Description',
    description_placeholder: 'e.g. Acme Eats — Braintree EUR',
    acquirer_merchant_id: 'Acquirer merchant ID',
    acquirer_hint: 'Optional — required together with MCC',
    mcc: 'MCC',
    mcc_hint: '4 digits, e.g. 5812',
    make_default: 'Use as the partner’s default',
    make_default_hint:
      'The partner’s new restaurants, and the ones with no gateway yet, are pinned to it. Leave unticked when it is one restaurant’s own acquirer.',
  },
  sca_modal: {
    title: '3DS2 registration — {{gateway}}',
    provision: 'Provision',
    irreversible_warning:
      'Irreversible: neither the merchant profile nor the SCA provider can be edited or deleted afterwards. A wrong acquirer BIN only surfaces at charge time. Verify every value with the acquiring bank before provisioning.',
    already_provisioned:
      'Already provisioned (provider token {{token}}). Provisioning again returns the existing registration unless Recreate is enabled.',
    type: {
      label: 'Provider type',
      hint: '“Test” simulates authentication outcomes and is rejected in production.',
      test: 'Test (SCA sandbox)',
      live: 'Live (Spreedly 3DS2 Global)',
    },
    country: {
      hint: 'The country the acquiring bank registered this merchant under — not necessarily where the partner is incorporated. Applies to every card network below.',
      differs_warning:
        'This is not the partner’s country ({{country}}). Correct if the acquirer registered the merchant elsewhere; otherwise check it, because a merchant profile cannot be edited once created.',
    },
    network_placeholder: 'Card network',
    add_network: 'Add card network',
    networks: {
      visa: 'Visa',
      mastercard: 'Mastercard',
      amex: 'American Express',
      discover: 'Discover',
      diners: 'Diners',
      jcb: 'JCB',
      dankort: 'Dankort',
      union_pay: 'UnionPay',
      cartes_bancaires: 'Cartes Bancaires',
    },
    fields: {
      acquirer_merchant_id: 'Acquirer merchant ID',
      acquirer_bin: 'Acquirer BIN',
      mcc: 'MCC',
      merchant_name: 'Merchant name',
      country_code: 'Country (ISO-2)',
      merchant_url: 'Merchant URL',
    },
    recreate: {
      label: 'Recreate the registration',
      hint: 'Abandons the current merchant profile and SCA provider forever and creates new ones.',
    },
  },
  check_modal: {
    title: 'Check gateway — {{gateway}}',
    run: 'Run check',
    done: 'Done',
    failed: 'The check could not be run.',
    types: {
      verify: 'Verify (no money moves)',
      test_payment: 'Test payment',
    },
    verify_unsupported: 'This gateway type does not support verification — run a test payment instead.',
    test_payment_warning:
      'A real charge is made on a live gateway and immediately refunded. On gateways in SCA markets the issuer may decline the non-3DS probe — that still proves the credentials reached the acquirer.',
    amount: 'Amount',
    amount_help: 'Between 1.00 and 5.00',
    check_passed: 'Check passed',
    check_failed: 'Check failed — see the steps below',
    legs: {
      verify: 'Verification',
      purchase: 'Charge',
      credit: 'Refund',
    },
    leg_passed: 'Passed',
    leg_failed: 'Failed',
  },
  card_fields: {
    script_failed: 'The Spreedly script could not be loaded. Check the network and try again.',
    full_name: 'Cardholder name',
    number: 'Card number',
    expiry: 'Expiry',
    cvv: 'CVV',
  },
};
